import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, ShieldCheck, AlarmClock, Plus } from "lucide-react";
import { api } from "../../lib/api";
import { formatINR, formatDate } from "../../lib/format";
import { useAuth } from "../../context/AuthContext";
import StatusBadge from "../../components/StatusBadge";
import { EditModeToggle } from "../../components/editor/EditModeToggle";
import { useEditModeSession } from "../../hooks/useEditModeSession";

function Stat({ icon: Icon, label, value, color, onClick, active }) {
  const colors = { indigo: "bg-[#FFF4ED] text-[#F97316]", emerald: "bg-emerald-50 text-emerald-600", amber: "bg-amber-50 text-amber-600" };
  return (
    <div onClick={onClick} className={`card-base p-6 cursor-pointer transition-all hover:shadow-md ${active ? "ring-2 ring-indigo-400" : ""}`}>
      <div className="flex items-center gap-3">
        <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${colors[color]}`}><Icon className="h-5 w-5" /></div>
        <div className="text-sm text-gray-500">{label}</div>
      </div>
      <div className="mt-3 text-3xl font-bold font-display">{value}</div>
    </div>
  );
}

function SlotsWidget() {
  // Slots widget hidden — unlimited agreements now available
  return null;
}

export default function LandlordHome() {
  const { user } = useAuth();
  const { isInEditMode } = useEditModeSession();
  const [rentals, setRentals] = useState([]);
  const [filter, setFilter] = useState(null); // null | "active" | "secured" | "pending"
  useEffect(() => { (async () => { const { data } = await api.get("/rentals"); setRentals(data); })(); }, []);
  const active = rentals.filter((r) => r.status === "active").length;
  const filteredRentals = filter === "active"
    ? rentals.filter(r => r.status === "active")
    : filter === "secured"
    ? rentals.filter(r => ["active", "checkout_pending", "rating_phase1", "settlement_pending"].includes(r.status))
    : filter === "pending"
    ? rentals.filter(r => ["agreement_pending", "settlement_pending", "checkout_pending"].includes(r.status))
    : rentals;
  const secured = rentals.reduce((s, r) => ["active", "checkout_pending", "rating_phase1", "settlement_pending"].includes(r.status) ? s + r.deposit_amount : s, 0);
  const pending = rentals.filter((r) => ["agreement_pending", "settlement_pending", "checkout_pending"].includes(r.status)).length;

  return (
    <div className={isInEditMode ? "edit-mode-active" : ""}>
      <EditModeToggle pageId="landlord_dashboard" />

      <div className="space-y-6" data-testid="landlord-home">
        <div>
          <h1 className="text-3xl font-bold font-display">Overview</h1>
        <p className="text-sm text-gray-500 mt-1">Dashboard overview of all your deposits and rentals.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Stat icon={Building2} label="Active properties" value={active} color="emerald" onClick={() => setFilter(filter === "active" ? null : "active")} active={filter === "active"} />
        <Stat icon={ShieldCheck} label="Deposits secured" value={formatINR(secured)} color="indigo" onClick={() => setFilter(filter === "secured" ? null : "secured")} active={filter === "secured"} />
        <Stat icon={AlarmClock} label="Pending actions" value={pending} color="amber" onClick={() => setFilter(filter === "pending" ? null : "pending")} active={filter === "pending"} />
      </div>

      {user?.landlord_type === "self_manage_properties" && <SlotsWidget />}

      <div className="card-base">
        {rentals.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            {user?.self_managed ? (
              <div>
                <p>No agreements yet.</p>
                <Link to="/app/agreement-builder" className="inline-flex mt-4 bg-[#F97316] hover:bg-[#EA580C] text-white px-4 py-2 rounded-lg text-sm font-medium">Create your first agreement →</Link>
              </div>
            ) : (
              <p>No agreements yet. Your broker will add you.</p>
            )}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs text-gray-500 uppercase tracking-wider bg-gray-50">
              <tr>
                <th className="text-left px-6 py-3">Property</th>
                <th className="text-left px-6 py-3">Tenant</th>
                <th className="text-right px-6 py-3">Deposit</th>
                <th className="text-left px-6 py-3">Lease ends</th>
                <th className="text-left px-6 py-3">Signature</th>
                <th className="text-left px-6 py-3">Status</th>
                <th className="px-6 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {filteredRentals.map((r) => {
                // PHASE 5: Check if all tenants have signed
                const allTenantsSigned = r.agreement?.tenant_signatures && r.agreement.tenant_signatures.every(sig => sig.signed_at);
                const isUnsigned = r.status === "agreement_pending" && r.agreement && (!allTenantsSigned || !r.agreement.signed_by_landlord);
                return (
                  <tr key={r.id} className={`border-t border-gray-100 hover:bg-gray-50 cursor-pointer ${isUnsigned ? "bg-amber-50/30" : ""}`} onClick={() => window.location.href = `/app/rental/${r.id}`}>
                    <td className="px-6 py-4 font-medium">{r.property_address}</td>
                    <td className="px-6 py-4 text-gray-600">{r.tenants_resolved?.[0]?.user?.name || "—"}</td>
                    <td className="px-6 py-4 text-right font-medium">{formatINR(r.deposit_amount)}</td>
                    <td className="px-6 py-4 text-gray-600">{formatDate(r.lease_end_date)}</td>
                    <td className="px-6 py-4">
                      {r.agreement && (
                        <div className="flex items-center gap-1.5 text-xs">
                          <span className={`h-2 w-2 rounded-full ${allTenantsSigned ? "bg-emerald-500" : "bg-amber-400"}`} title={allTenantsSigned ? "All tenants signed" : "Tenant signature pending"} />
                          <span className={`h-2 w-2 rounded-full ${r.agreement.signed_by_landlord ? "bg-emerald-500" : "bg-amber-400"}`} title={r.agreement.signed_by_landlord ? "Your signature" : "Pending your signature"} />
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4"><StatusBadge status={r.status} /></td>
                    <td className="px-6 py-4 text-right"><Link to={`/app/rental/${r.id}`} className="text-[#F97316]">Open →</Link></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      </div>
    </div>
  );
}